import { useState, useEffect } from 'react'
import { Button, Toggle, Chip, Alert } from '@/components/ui'
import { Save, Plus, Shield, Building2, FileText, SlidersHorizontal, Pencil, Loader2, CheckCircle2 } from 'lucide-react'
import { cn } from '@/lib/utils'
import {
  useCuentasPoint, useToggleCuenta,
  useRazonesPoint, useToggleRazon,
  useAntifraudeConfig, useActualizarAntifraudeConfig,
  type VentanaFecha, type AntifraudeConfig,
} from '@/lib/api/antifraude'

export function Antifraude() {
  const cuentas = useCuentasPoint()
  const razones = useRazonesPoint()
  const config = useAntifraudeConfig()

  if (cuentas.isLoading || razones.isLoading || config.isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-6 h-6 animate-spin text-amber-500" />
        <span className="ml-2 text-slate-500">Cargando configuración anti-fraude…</span>
      </div>
    )
  }

  if (cuentas.error || razones.error || config.error) {
    const err = (cuentas.error ?? razones.error ?? config.error) as Error
    return <Alert variant="danger" title="Error al cargar">{err.message}</Alert>
  }

  return (
    <div className="space-y-5">
      <Alert variant="info" title="Config crítica">
        Los cambios aplican a comprobantes recibidos a partir del guardado. Los pendientes de revisión conservan la config con la que fueron escaneados por la IA.
      </Alert>

      <CuentasSection cuentas={cuentas.data ?? []} />
      <RazonesSection razones={razones.data ?? []} />
      <UmbralesSection config={config.data} />
    </div>
  )
}

// ─── Sección Cuentas ─────────────────────────────────────────────────────

function CuentasSection({ cuentas }: { cuentas: ReturnType<typeof useCuentasPoint>['data'] extends infer T ? NonNullable<T> : never }) {
  const toggleM = useToggleCuenta()

  return (
    <section className="bg-white border border-slate-200 rounded-xl overflow-hidden">
      <header className="px-5 py-4 border-b border-slate-200 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Building2 className="w-5 h-5 text-blue-500" />
          <div>
            <h2 className="font-display text-lg text-navy-700">Cuentas bancarias oficiales de Point</h2>
            <p className="text-[11px] text-slate-500">
              Solo se validan comprobantes cuyo destino coincida con una de estas cuentas · {cuentas.length} cuentas
            </p>
          </div>
        </div>
        <Button variant="outline" size="sm" disabled>
          <Plus className="w-3.5 h-3.5" /> Agregar (próximo)
        </Button>
      </header>
      <table className="w-full text-sm">
        <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-600 font-semibold">
          <tr>
            <th className="text-left px-5 py-2">Banco</th>
            <th className="text-left px-5 py-2">Tipo</th>
            <th className="text-left px-5 py-2">Número</th>
            <th className="text-left px-5 py-2">Titular</th>
            <th className="text-left px-5 py-2">Activa</th>
            <th className="text-right px-5 py-2">Acciones</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {cuentas.map(c => (
            <tr key={c.id} className={cn('hover:bg-slate-50', !c.activo && 'opacity-50')}>
              <td className="px-5 py-3 font-semibold text-navy-700">{c.banco}</td>
              <td className="px-5 py-3 text-slate-700 capitalize">{c.tipo}</td>
              <td className="px-5 py-3 font-mono text-slate-700">{c.numero}</td>
              <td className="px-5 py-3 text-slate-700 text-xs">{c.titular}</td>
              <td className="px-5 py-3">
                <Toggle
                  on={c.activo}
                  disabled={toggleM.isPending}
                  onChange={v => toggleM.mutate({ id: c.id, activo: v })}
                />
              </td>
              <td className="px-5 py-3 text-right">
                <button className="w-7 h-7 flex items-center justify-center rounded-md hover:bg-slate-100 text-slate-500 ml-auto" disabled>
                  <Pencil className="w-3.5 h-3.5" />
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  )
}

// ─── Sección Razones ─────────────────────────────────────────────────────

function RazonesSection({ razones }: { razones: ReturnType<typeof useRazonesPoint>['data'] extends infer T ? NonNullable<T> : never }) {
  const toggleM = useToggleRazon()
  return (
    <section className="bg-white border border-slate-200 rounded-xl overflow-hidden">
      <header className="px-5 py-4 border-b border-slate-200 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <FileText className="w-5 h-5 text-violet-500" />
          <div>
            <h2 className="font-display text-lg text-navy-700">Razones sociales aceptadas</h2>
            <p className="text-[11px] text-slate-500">
              El beneficiario del comprobante debe coincidir con alguna de estas vía regex · {razones.length} razones
            </p>
          </div>
        </div>
        <Button variant="outline" size="sm" disabled>
          <Plus className="w-3.5 h-3.5" /> Agregar (próximo)
        </Button>
      </header>
      <ul className="divide-y divide-slate-100">
        {razones.map(r => (
          <li key={r.id} className={cn('px-5 py-3 flex items-center gap-4', !r.activo && 'opacity-50')}>
            <div className="flex-1 min-w-0">
              <div className="font-semibold text-navy-700">{r.razon_social_canonica}</div>
              <div className="text-xs text-slate-500 mt-0.5 flex items-center gap-2 flex-wrap">
                {r.ruc && <Chip variant="default">RUC {r.ruc}</Chip>}
                <span className="font-mono bg-slate-100 px-1.5 py-0.5 rounded text-[11px]">
                  /{r.patron_matching}/i
                </span>
                {r.notas && <span>· {r.notas}</span>}
              </div>
            </div>
            <Toggle
              on={r.activo}
              disabled={toggleM.isPending}
              onChange={v => toggleM.mutate({ id: r.id, activo: v })}
            />
          </li>
        ))}
      </ul>
    </section>
  )
}

// ─── Sección Umbrales ────────────────────────────────────────────────────

function UmbralesSection({ config }: { config: AntifraudeConfig | null | undefined }) {
  const mutation = useActualizarAntifraudeConfig()

  // Estado local optimista
  const [draft, setDraft] = useState<Partial<AntifraudeConfig>>({})
  const [dirty, setDirty] = useState(false)

  useEffect(() => {
    setDraft({})
    setDirty(false)
  }, [config?.actualizado_en])

  if (!config) return null

  const merged: AntifraudeConfig = { ...config, ...draft }

  const update = <K extends keyof AntifraudeConfig>(key: K, value: AntifraudeConfig[K]) => {
    setDraft(d => ({ ...d, [key]: value }))
    setDirty(true)
  }

  const guardar = async () => {
    if (Object.keys(draft).length === 0) return
    // El RPC necesita strings para numeric/boolean/text (jsonb_each_text los extrae así)
    const toSend = Object.fromEntries(
      Object.entries(draft).map(([k, v]) => [k, v])
    )
    await mutation.mutateAsync(toSend as Partial<Omit<AntifraudeConfig, 'actualizado_en' | 'actualizado_por'>>)
    setDirty(false)
  }

  const descartar = () => {
    setDraft({})
    setDirty(false)
  }

  const ventanas: { k: VentanaFecha; l: string }[] = [
    { k: 'hoy', l: 'Hoy' },
    { k: '7d', l: 'Últimos 7 días' },
    { k: 'mes_actual', l: 'Mes actual' },
    { k: '30d', l: 'Últimos 30 días' },
    { k: 'custom', l: 'Custom' },
  ]

  return (
    <>
      <section className="bg-white border border-slate-200 rounded-xl p-5 space-y-5">
        <header className="flex items-center gap-2">
          <SlidersHorizontal className="w-5 h-5 text-amber-500" />
          <h2 className="font-display text-lg text-navy-700">Umbrales de validación automática</h2>
        </header>

        <div>
          <div className="flex items-center justify-between mb-1">
            <label className="text-xs font-semibold text-slate-700 uppercase tracking-wider">
              Confianza mínima IA
            </label>
            <span className="font-mono text-lg font-semibold text-navy-700">
              {Math.round((merged.confianza_minima_ia ?? 0.85) * 100)}%
            </span>
          </div>
          <input
            type="range"
            min={50}
            max={100}
            step={1}
            value={Math.round((merged.confianza_minima_ia ?? 0.85) * 100)}
            onChange={e => update('confianza_minima_ia', Number(e.target.value) / 100)}
            className="w-full accent-amber-500"
          />
          <div className="flex justify-between text-[10px] text-slate-500 mt-1">
            <span>50% (permisivo)</span>
            <span>85% (default)</span>
            <span>100% (estricto)</span>
          </div>
          <p className="text-[11px] text-slate-500 mt-1">
            Si la IA tiene menos de este % de confianza → revisión humana obligatoria.
          </p>
        </div>

        <div>
          <label className="text-xs font-semibold text-slate-700 uppercase tracking-wider mb-2 block">
            Ventana de fecha permitida
          </label>
          <div className="flex flex-wrap gap-2">
            {ventanas.map(opt => (
              <button
                key={opt.k}
                onClick={() => update('ventana_fecha', opt.k)}
                className={cn(
                  'px-3 py-1.5 rounded-md text-sm font-medium transition',
                  merged.ventana_fecha === opt.k
                    ? 'bg-emerald-500 text-white'
                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                )}
              >
                {opt.l}
              </button>
            ))}
          </div>
          <p className="text-[11px] text-slate-500 mt-2">
            Fechas fuera de esta ventana → el caso va a revisión humana automática.
          </p>
        </div>

        <ToggleRow
          label="Requiere cliente vinculado"
          desc="Solo se auto-valida si quien envió está en BD de clientes Point."
          on={merged.requiere_cliente_vinculado}
          onChange={v => update('requiere_cliente_vinculado', v)}
        />
        <ToggleRow
          label="Cross-check con saldo BD"
          desc="Verificar que el monto del comprobante sea razonable vs el saldo vencido del cliente."
          on={merged.cross_check_saldo}
          onChange={v => update('cross_check_saldo', v)}
        />
        <ToggleRow
          label="Rechazar manuscritos automáticamente"
          desc="Comprobantes escritos a mano → siempre revisión humana (nunca auto-aprobación)."
          on={merged.rechazar_manuscritos}
          onChange={v => update('rechazar_manuscritos', v)}
        />
        <ToggleRow
          label="Permitir pagos parciales"
          desc="Aceptar abonos menores al saldo total (si está desactivado, solo saldo exacto se auto-valida)."
          on={merged.permitir_pagos_parciales}
          onChange={v => update('permitir_pagos_parciales', v)}
        />
      </section>

      <div className="sticky bottom-4 flex items-center justify-between bg-slate-50 rounded-xl p-4 shadow-md">
        <div className="flex items-center gap-3 text-xs">
          {dirty ? (
            <Chip variant="amber">● Cambios sin guardar</Chip>
          ) : mutation.isSuccess ? (
            <Chip variant="emerald"><CheckCircle2 className="w-3 h-3 mr-1 inline" /> Guardado</Chip>
          ) : (
            <Chip variant="default">
              Última edición: {new Date(config.actualizado_en).toLocaleString('es-EC')}
            </Chip>
          )}
          {mutation.isError && (
            <span className="text-rose-600">{(mutation.error as Error).message}</span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={descartar} disabled={!dirty || mutation.isPending}>
            Descartar
          </Button>
          <Button variant="rose" size="sm" onClick={guardar} disabled={!dirty || mutation.isPending}>
            {mutation.isPending ? (
              <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Guardando…</>
            ) : (
              <><Save className="w-3.5 h-3.5" /> Guardar</>
            )}
          </Button>
        </div>
      </div>
    </>
  )
}

function ToggleRow({ label, desc, on, onChange }: {
  label: string
  desc: string
  on: boolean
  onChange: (v: boolean) => void
}) {
  return (
    <div className="flex items-start justify-between gap-4 pt-3 border-t border-slate-100 first:border-t-0 first:pt-0">
      <div className="flex-1 min-w-0">
        <div className="font-semibold text-navy-700 text-sm flex items-center gap-2">
          <Shield className="w-3.5 h-3.5 text-slate-400" /> {label}
        </div>
        <p className="text-xs text-slate-500 mt-0.5 pl-5">{desc}</p>
      </div>
      <Toggle on={on} onChange={onChange} />
    </div>
  )
}
